import { z } from 'zod';
import type { IdealQuestionDto } from './onboarding';
import type { LibraryCoverageDto, LibraryGround, LibraryRefDto } from './library-refs';
import { OFFICE_CODES, type OfficeBody, type OfficeCode } from './offices';

/**
 * L'impresa (ADR 0021): anagrafica, organi e cariche, attività economiche svolte nelle sedi. Sono il contesto da cui
 * dipendono gli standard di ogni attività: i dati vengono dalle fonti (visura, documenti dell'azienda, persona, pagine
 * con la partita IVA), mai inventati.
 */

/** Campi dell'anagrafica: quelli che la legge definisce per un'impresa (non categorie in cui classificarla). */
export const IDENTITY_FIELDS = [
  'ragione_sociale',
  'forma_giuridica',
  'codice_fiscale',
  'partita_iva',
  'rea',
  'pec',
  'data_costituzione',
  'capitale_sociale',
  'ateco_primario',
  'ateco_secondari',
  'oggetto_sociale',
  'stato_registro',
  'addetti',
  'addetti_al',
  'fatturato',
  'totale_bilancio',
  'anno_bilancio',
  'paese',
] as const;
export type IdentityField = (typeof IDENTITY_FIELDS)[number];

/**
 * Le forme giuridiche previste dall'ordinamento italiano: la lettura capisce dal testo quale è e sceglie una di queste,
 * non copia le parole del documento.
 */
export const LEGAL_FORMS = [
  'Ditta individuale',
  'Impresa familiare',
  'Società semplice (s.s.)',
  'Società in nome collettivo (s.n.c.)',
  'Società in accomandita semplice (s.a.s.)',
  'Società a responsabilità limitata (s.r.l.)',
  'Società a responsabilità limitata semplificata (s.r.l.s.)',
  'Società a responsabilità limitata con socio unico',
  'Società per azioni (s.p.a.)',
  'Società in accomandita per azioni (s.a.p.a.)',
  'Società cooperativa',
  'Società cooperativa sociale',
  'Società consortile',
  'Consorzio',
  'Società tra professionisti (s.t.p.)',
  'Società benefit',
  'Studio associato',
  'Associazione',
  'Fondazione',
  'Ente pubblico',
  'GEIE',
  'Società estera',
] as const;
export type LegalForm = (typeof LEGAL_FORMS)[number];

/** La voce dell'elenco così come la scrive chi la sceglie (maiuscole e spazi non contano), o null se non è nell'elenco. */
export function legalForm(value: string | null | undefined): LegalForm | null {
  const v = value?.trim().toLowerCase().replace(/\s+/g, ' ');
  return LEGAL_FORMS.find((f) => f.toLowerCase() === v) ?? null;
}

/**
 * I dati dell'impresa che la persona conferma nel passo «L'impresa» della configurazione guidata: quelli che chiunque
 * in azienda sa riconoscere. Codici e dati di registro restano consultabili ma non si chiedono. `in_breve` = di cosa si
 * occupa, detto in parole semplici.
 */
export const COMPANY_KEY_FACTS = [
  'ragione_sociale',
  'in_breve',
  'forma_giuridica',
  'addetti',
  'data_costituzione',
  'pec',
] as const satisfies readonly (IdentityField | 'in_breve')[];
export type CompanyKeyFact = (typeof COMPANY_KEY_FACTS)[number];

/** sito = il sito dell'azienda; registro = una pagina pubblica in cui compare la partita IVA (non del sito). */
/** online = pagina pubblica sulla sede, senza partita IVA (ADR 0044). */
/** «supposto»: lo sceglie il cervello per l'azienda ideale, quando nessuna fonte lo dice (ADR 0062). */
export const COMPANY_SOURCES = ['visura', 'persona', 'documento', 'posta', 'sito', 'registro', 'online', 'supposto'] as const;
export type CompanySource = (typeof COMPANY_SOURCES)[number];

export interface FieldSource {
  fonte: CompanySource;
  /** Documento, pagina o messaggio da cui viene. */
  rif: string | null;
  /** Le parole della fonte. */
  citazione: string | null;
  aggiornato: string;
  /** Per un dato ricavato da più fonti insieme (come `in_breve`): quali. */
  da?: CompanySource[];
}

export interface CompanyIdentity {
  ragione_sociale: string | null;
  forma_giuridica: string | null;
  codice_fiscale: string | null;
  partita_iva: string | null;
  rea: string | null;
  pec: string | null;
  data_costituzione: string | null;
  capitale_sociale: string | null;
  ateco_primario: string | null;
  ateco_secondari: string[];
  oggetto_sociale: string | null;
  stato_registro: string | null;
  addetti: number | null;
  addetti_al: string | null;
  /** Euro dell'esercizio `anno_bilancio` (ADR 0127). */
  fatturato: number | null;
  totale_bilancio: number | null;
  anno_bilancio: number | null;
  paese: string | null;
  /** Di cosa si occupa, in parole semplici: scritto dal sistema dai dati dell'impresa, o dalla persona. */
  in_breve: string | null;
  fonti: Partial<Record<IdentityField | 'in_breve', FieldSource>>;
}

const text = (max: number) => z.string().trim().min(1).max(max);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data AAAA-MM-GG');

/** La persona corregge o completa l'anagrafica: ciò che scrive non lo cambia più una lettura. null = cancella. */
export const UpdateIdentitySchema = z
  .object({
    ragione_sociale: text(300).nullable(),
    forma_giuridica: z.enum(LEGAL_FORMS).nullable(),
    codice_fiscale: text(20).nullable(),
    partita_iva: text(20).nullable(),
    rea: text(60).nullable(),
    pec: text(254).nullable(),
    data_costituzione: day.nullable(),
    capitale_sociale: text(200).nullable(),
    ateco_primario: text(300).nullable(),
    ateco_secondari: z.array(text(300)).max(30),
    oggetto_sociale: text(8000).nullable(),
    stato_registro: text(200).nullable(),
    addetti: z.number().int().min(0).max(1_000_000).nullable(),
    addetti_al: day.nullable(),
    fatturato: z.number().int().min(0).max(1e13).nullable(),
    totale_bilancio: z.number().int().min(0).max(1e13).nullable(),
    anno_bilancio: z.number().int().min(1900).max(2100).nullable(),
    paese: text(80).nullable(),
    /** null = lo riscrive il sistema dai dati dell'impresa. */
    in_breve: text(600).nullable(),
  })
  .partial();
export type UpdateIdentityInput = z.infer<typeof UpdateIdentitySchema>;

export const GOVERNANCE_STATUSES = ['in_carica', 'cessata'] as const;

export interface GovernanceRoleDto {
  id: string;
  titolare: string;
  tipo_titolare: 'persona' | 'societa';
  codice_fiscale: string | null;
  /** Codice carica del Registro delle imprese (ADR 0124); null finché la carica non è riordinata. */
  codice: OfficeCode | null;
  /** Il nome della carica nella codifica, o la carica scritta se non ha ancora il codice. */
  carica: string;
  /** La carica come la scrive la fonte. */
  carica_scritta: string;
  /** L'organo del codice, o quello scritto dalla fonte se la carica non ha ancora il codice. */
  organo: OfficeBody | string | null;
  poteri: string | null;
  quota: string | null;
  nominato_il: string | null;
  durata: string | null;
  fine: string | null;
  /** La persona dell'azienda che la ricopre, se ci lavora. */
  persona_id: string | null;
  fonte: Exclude<CompanySource, 'posta'>;
  rif: string | null;
  stato: (typeof GOVERNANCE_STATUSES)[number];
  /** Letta da un documento, non risulta nella visura di questa data (ADR 0124): non vuol dire che sia finita. */
  non_in_visura_dal: string | null;
}

export const GovernanceInputSchema = z.object({
  titolare: text(300),
  tipo_titolare: z.enum(['persona', 'societa']).default('persona'),
  codice_fiscale: text(20).nullable().optional(),
  carica: text(300),
  codice: z.enum(Object.keys(OFFICE_CODES) as [OfficeCode, ...OfficeCode[]]).nullable().optional(),
  organo: text(300).nullable().optional(),
  poteri: text(4000).nullable().optional(),
  quota: text(200).nullable().optional(),
  nominato_il: day.nullable().optional(),
  durata: text(300).nullable().optional(),
  fine: day.nullable().optional(),
  persona_id: z.string().uuid().nullable().optional(),
  stato: z.enum(GOVERNANCE_STATUSES).optional(),
});
export type GovernanceInput = z.infer<typeof GovernanceInputSchema>;
export const UpdateGovernanceSchema = GovernanceInputSchema.partial();
export type UpdateGovernanceInput = z.infer<typeof UpdateGovernanceSchema>;

export const ACTIVITY_STATUSES = ['da_confermare', 'attiva', 'cessata'] as const;
/** Stato di un'attività in una sede (ADR 0044): esclusa = non si fa in questa sede (nessuna lettura la rimette). */
export const SITE_ACTIVITY_STATUSES = ['da_confermare', 'attiva', 'esclusa'] as const;
export type SiteActivityStatus = (typeof SITE_ACTIVITY_STATUSES)[number];

/**
 * Da cosa si sa che un'attività si svolge in una sede (ADR 0045): codice della sede e fonte online, solo una fonte online
 * (la visura non ha il codice), solo il codice (nessuna fonte ne parla).
 */
export const ACTIVITY_PROOFS = ['visura_e_online', 'solo_online', 'solo_visura'] as const;
export type ActivityProof = (typeof ACTIVITY_PROOFS)[number];

/** Un'attività in una sede: stato, come vi si svolge, da dove viene. */
export interface ActivityAtSiteDto {
  site_id: string;
  stato: SiteActivityStatus;
  /** null = detta dalla persona o da una lettura senza sintesi. */
  prova: ActivityProof | null;
  descrizione: string | null;
  fonte: CompanySource;
  rif: string | null;
  /** Quanto pesa l'attività nel lavoro della sede, 0-100, ipotesi del consulente (ADR 0089); null se non stimato. */
  peso?: number | null;
}

export interface EconomicActivityDto {
  id: string;
  key: string;
  nome: string;
  ateco: string | null;
  descrizione: string | null;
  /** Un emoji scelto dal motore per riconoscerla (ADR 0045). */
  icona: string | null;
  /** L'attività prevalente dell'impresa. */
  prevalente: boolean;
  fonte: CompanySource;
  rif: string | null;
  stato: (typeof ACTIVITY_STATUSES)[number];
  /** Sedi in cui si svolge (id), escluse quelle in cui la persona ha detto che non si fa. */
  sedi: string[];
  /** Ogni sede legata, anche esclusa, con lo stato e come vi si svolge (ADR 0044). */
  in_sede: ActivityAtSiteDto[];
}

export const EconomicActivityInputSchema = z.object({
  nome: text(300),
  ateco: text(300).nullable().optional(),
  descrizione: text(4000).nullable().optional(),
  prevalente: z.boolean().optional(),
  /** Sedi in cui si svolge (sostituiscono quelle di prima). */
  sedi: z.array(z.string().uuid()).max(200).optional(),
});
export type EconomicActivityInput = z.infer<typeof EconomicActivityInputSchema>;
export const UpdateEconomicActivitySchema = EconomicActivityInputSchema.partial().extend({
  /** La persona conferma un'attività trovata dal sistema, annulla la conferma (da_confermare) o la segna cessata. */
  stato: z.enum(ACTIVITY_STATUSES).optional(),
});
export type UpdateEconomicActivityInput = z.infer<typeof UpdateEconomicActivitySchema>;

/** La persona conferma un'attività in una sede, dice che lì non si fa, o scrive come vi si svolge (ADR 0044). */
export const UpdateActivityAtSiteSchema = z
  .object({
    stato: z.enum(SITE_ACTIVITY_STATUSES).optional(),
    descrizione: text(2000).nullable().optional(),
  })
  .refine((v) => v.stato !== undefined || v.descrizione !== undefined, { message: 'Indica lo stato o la descrizione' });
export type UpdateActivityAtSiteInput = z.infer<typeof UpdateActivityAtSiteSchema>;

/** Scheda dell'impresa: anagrafica, organi e cariche, attività economiche. */
export interface CompanyCardDto {
  identita: CompanyIdentity;
  cariche: GovernanceRoleDto[];
  attivita: EconomicActivityDto[];
  /** Cosa manca per conoscere l'impresa come la conoscerebbe un consulente: i documenti di partenza. */
  mancanti: string[];
  /** Quali documenti di partenza sono arrivati (letti come tali). */
  documenti_di_partenza: { visura: boolean; bilancio: boolean; elenco_personale: boolean };
}

/** Impianti e attrezzature (ADR 0023). */
/** atteso = da confermare; da_verificare = la persona non sa se c'è (ADR 0031). */
export const EQUIPMENT_STATUSES = ['atteso', 'presente', 'non_presente', 'da_verificare'] as const;
export type EquipmentStatus = (typeof EQUIPMENT_STATUSES)[number];

/**
 * Una riga del registro di un impianto (ADR 0134): cosa si fa, ogni quanto, quale ruolo lo fa, la prova e perché, cioè la
 * norma (base legge) o la buona prassi con la sua fonte (base buona_prassi). Le righe di prima dell'ADR non hanno la base:
 * valgono come legge.
 */
export interface EquipmentObligation {
  /** Il riferimento: la norma per la legge, la fonte (norma tecnica, guida, manuale del fabbricante) per la buona prassi. */
  norma: string;
  /** Cosa si fa. */
  descrizione: string;
  periodicita: string | null;
  /** Il ruolo che lo fa, interno o esterno: mai una persona. */
  chi: string | null;
  prova: string | null;
  base?: 'legge' | 'buona_prassi';
  /** motore = dal riferimento dell'attività; produttore = dal libretto. */
  fonte: 'motore' | 'produttore';
}

export interface EquipmentDto {
  id: string;
  key: string;
  /** Sede (null = dell'impresa). */
  sede: string | null;
  nome: string;
  tipo: string | null;
  /** Un emoji scelto dal motore per riconoscerlo (null finché non l'ha scelto). */
  icona: string | null;
  perche: string | null;
  condizione: string | null;
  /** Attività economiche che lo richiedono (id). */
  attivita: string[];
  stato: EquipmentStatus;
  quantita: number | null;
  /** Quanti ce ne sono secondo il cervello, stimato dai fatti della sede e dichiarato come stima (ADR 0134); vale finché il titolare non scrive quanti. */
  stima: string | null;
  marca: string | null;
  modello: string | null;
  matricola: string | null;
  installato_il: string | null;
  obblighi: EquipmentObligation[];
  manutenzione: Array<{ attivita: string; cadenza: string }>;
  libretto: string | null;
  libretto_stato: 'non_cercato' | 'trovato' | 'non_trovato' | 'non_identificato';
  fonte: 'motore' | 'persona' | 'documento' | 'posta';
  /** Le voci della libreria da cui nasce (ADR 0130), solo quelle che esistono nel catalogo. */
  libreria?: LibraryRefDto[];
  /** Perché vale (ADR 0130): un riferimento della libreria, una norma verificata, un fatto dell'azienda, o nessuna base. */
  fondata?: LibraryGround | null;
}

export interface EquipmentState {
  data: EquipmentDto[];
  /** Il motore sta scegliendo le icone degli impianti che non ce l'hanno. */
  icone_in_corso: boolean;
  /** Sedi di cui il riferimento si sta preparando o è da rifare. */
  in_preparazione: string[];
  /** Errori dell'ultima preparazione, per sede. */
  errori: Record<string, string>;
  /**
   * Le domande del cervello al titolare, per sede (`azienda` per l'impresa senza sedi), con la risposta probabile
   * (ADR 0065). Solo le sedi con un riferimento pronto: vuoto vuol dire nessuna domanda.
   */
  domande: Record<string, IdealQuestionDto[]>;
  /** Impianti aggiunti dal titolare che il cervello sta completando (ADR 0098): perché, attività e obblighi. */
  in_completamento?: string[];
  /** Quanti impianti nascono da voci della libreria, divisi per fonte (ADR 0130). */
  copertura?: LibraryCoverageDto;
}

export const UpdateEquipmentSchema = z
  .object({
    stato: z.enum(EQUIPMENT_STATUSES),
    quantita: z.number().int().min(0).max(100_000).nullable(),
    marca: text(200).nullable(),
    modello: text(200).nullable(),
    matricola: text(200).nullable(),
    installato_il: day.nullable(),
  })
  .partial();
export type UpdateEquipmentInput = z.infer<typeof UpdateEquipmentSchema>;

export const CreateEquipmentSchema = z.object({
  nome: text(300),
  sede: z.string().uuid().nullable().default(null),
  tipo: text(200).nullable().optional(),
  quantita: z.number().int().min(0).max(100_000).nullable().optional(),
  marca: text(200).nullable().optional(),
  modello: text(200).nullable().optional(),
});
export type CreateEquipmentInput = z.infer<typeof CreateEquipmentSchema>;

/**
 * L'atto costitutivo ricostruito dalla visura (ADR 0077): sezioni fisse, ogni voce con la citazione esatta del testo, e
 * i controlli che dicono se le sezioni tornano con ciò che la visura conta.
 */
export const REGISTRY_SECTIONS = ['identita', 'oggetto', 'capitale_soci', 'amministrazione', 'sedi', 'addetti', 'albi_licenze', 'eventi'] as const;
export type RegistrySection = (typeof REGISTRY_SECTIONS)[number];

export interface RegistryItemDto {
  campo: string;
  valore: string;
  citazione: string;
}

export interface RegistryRecordDto {
  /** assente: nessuna visura; in_lettura: il consulente la sta leggendo; pronto; errore. */
  stato: 'assente' | 'in_lettura' | 'pronto' | 'errore';
  /** La data della visura, AAAA-MM-GG, se scritta nel documento. */
  data: string | null;
  sezioni: Array<{ chiave: RegistrySection; titolo: string; voci: RegistryItemDto[] }>;
  controlli: Array<{ cosa: string; ok: boolean; dettaglio: string }>;
  /** Le contraddizioni dentro la visura, rilette e non corrette (contratto 2.0, art. 5.4; ADR 0171). */
  contraddizioni?: Array<{ chiave: string; sezione: RegistrySection; cosa: string; dettaglio: string; valori: Array<{ voce: string; valore: string; citazione: string }>; riletta: boolean }>;
}
